import { Navigate } from 'react-router-dom';

import { Overview } from '@/pages/overview';
import { Orders } from '@/pages/orders';
import { Results } from '@/pages/results';
import { Customers } from '@/pages/customers';
import { Ads } from '@/pages/ads';
import { LocalAdsInsight } from '@/pages/ads/insights';
import { Channels } from '@/pages/channels';
import { ChannelDetail } from '@/pages/channels/ChannelDetail';
import { ChannelManage } from '@/pages/channels/ChannelManage';
import { ChannelSettings } from '@/pages/channels/ChannelSettings';
import { ChannelSetup } from '@/pages/channels/ChannelSetup';
import { Inventory } from '@/pages/inventory';
import { InventoryConnection } from '@/pages/inventory/connection';
import { ManageItems } from '@/pages/inventory/manage';
import { ProductDetail } from '@/pages/inventory/manage/product-detail';
import { Settings } from '@/pages/settings';
import { LocalPotential } from '@/pages/local-potential';
import { LearnUnifiedCommerce } from '@/pages/learn/unified-commerce';
import { AddStore } from '@/pages/add-store';
import { NoShops } from '@/pages/errors/NoShops';
import { NotFound } from '@/pages/errors/NotFound';

/**
 * Shop-scoped pages. Shared verbatim between the flat single-shop tree (`/`)
 * and the multi-shop tree (`/:shopId`) so the two stay in lock-step.
 *
 * Top-level navigable pages carry a `handle` with the metadata the rest of the
 * app needs about them — `label` for display and `assistantPurpose`, a plain
 * sentence describing what the page is for. This is the single source of truth
 * for "where things live": the AI assistant's dashboard map is derived from it
 * (see `lib/dashboard-map.js`), so adding a page (with a `handle`) is all it
 * takes for the assistant to know about it. Routes without a `handle` (detail
 * routes, redirects, the catch-all) are intentionally excluded.
 */
export const appChildren = [
	{
		index: true,
		element: <Overview />,
		handle: {
			label: 'Overview',
			assistantPurpose: 'The home dashboard — local potential score, key first-30-days actions and a snapshot of performance.'
		}
	},
	{
		path: 'orders',
		element: <Orders />,
		handle: {
			label: 'Orders',
			assistantPurpose:
				'View, track and fulfil customer orders (including Local Checkout / reservation orders) — mark them as collected, cancelled or refunded.'
		}
	},
	{
		path: 'results',
		element: <Results />,
		handle: { label: 'Results', assistantPurpose: 'Performance analytics — views, clicks and engagement across your channels.' }
	},
	{ path: 'customers', element: <Customers /> },
	{
		path: 'ads',
		element: <Ads />,
		handle: { label: 'Ads', assistantPurpose: 'Set up and manage Local Ads on Google — daily budget, status and ad performance.' }
	},
	{ path: 'ads/insights/:metric', element: <LocalAdsInsight /> },
	{
		path: 'channels',
		element: <Channels />,
		handle: {
			label: 'Channels',
			assistantPurpose: 'Turn sales channels on or off (Google, Meta, Deliveroo, Uber Eats and more) and manage each channel’s setup.'
		}
	},
	{ path: 'channels/:id', element: <ChannelDetail /> },
	{ path: 'channels/:id/manage', element: <ChannelManage /> },
	{ path: 'channels/:id/settings', element: <ChannelSettings /> },
	{ path: 'channels/:id/setup', element: <ChannelSetup /> },
	{
		path: 'inventory',
		element: <Inventory />,
		handle: {
			label: 'Inventory',
			assistantPurpose:
				'Your product feed — see which products are live, fix invisible products and check the connection to your POS.'
		}
	},
	{ path: 'inventory/connection', element: <InventoryConnection /> },
	{ path: 'inventory/manage', element: <ManageItems /> },
	{ path: 'inventory/manage/:barcode', element: <ProductDetail /> },
	{ path: 'local-potential', element: <LocalPotential /> },
	{ path: 'learn/unified-commerce', element: <LearnUnifiedCommerce /> },
	// Settings is shop-scoped: store-tab sub-pages (Storefront, Billing,
	// the Inventory feed and Channels nav cards) are inherently per-shop,
	// and the account-tab sub-pages (Team, Notifications, Security) ride
	// along under the same prefix so navigation stays consistent when
	// switching shops mid-flow.
	{
		path: 'settings/*',
		element: <Settings />,
		handle: {
			label: 'Settings',
			assistantPurpose: 'Store settings (storefront info, billing & plan) and account settings (team, notifications, security).'
		}
	},
	{
		path: 'add-store',
		element: <AddStore />,
		handle: {
			label: 'Add another store',
			assistantPurpose:
				'Add another store (location) to your account — your plan and billing carry over; you just pick the new location.'
		}
	},
	// legacy redirects from the previous shared dashboard
	{ path: 'home/:shopId?', element: <Navigate to="/" replace /> },
	{ path: 'dashboard/:shopId?', element: <Navigate to="/" replace /> },
	{ path: 'insights/*', element: <Navigate to="results" replace /> },
	{ path: 'reports/*', element: <Navigate to="settings" replace /> },
	{ path: 'no-shops', element: <NoShops /> },
	{ path: '*', element: <NotFound /> }
];
